import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { statsApi, usersApi, benchmarksApi, billingsApi, aiApi } from "../lib/api";
import KPICard from "../components/KPICard";
import { EncryptionRotationCard } from "../components/EncryptionRotationCard";
import BenchmarkGauge from "../components/BenchmarkGauge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Users, TrendingUp, TrendingDown, Target, ArrowRight, Loader2, Calendar, DollarSign, Sparkles, Download, FileSpreadsheet, Wallet, Printer } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
} from "recharts";
import * as XLSX from "xlsx";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 0, label: "All Year (YTD)" },
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [firmData, setFirmData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [employeeStats, setEmployeeStats] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [availableYears, setAvailableYears] = useState([]);
  const [partnerBillings, setPartnerBillings] = useState({ current: 0, previous: 0, byPartner: {}, prevByPartner: {} });
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [financials, setFinancials] = useState({ current: null, previous: null, chartData: [] });
  const [showPrintView, setShowPrintView] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (selectedYear !== null) {
      loadDashboardData(selectedYear, selectedMonth || null);
    }
  }, [selectedYear, selectedMonth]);

  const loadAiInsights = async () => {
    setLoadingInsights(true);
    try {
      const res = await aiApi.getInsights(selectedYear);
      setAiInsights(res.data);
    } catch (error) {
      toast.error("Failed to generate AI insights");
      console.error("AI insights error:", error);
    } finally {
      setLoadingInsights(false);
    }
  };

  const loadDashboardData = async (year = null, month = null) => {
    try {
      setLoading(true);
      const selectedYearValue = year || new Date().getFullYear();
      const monthParam = month && month > 0 ? month : null;
      const actualCurrentYear = new Date().getFullYear();
      
      // For YTD comparison logic:
      // - If viewing CURRENT year (2026) YTD: compare to completed months of previous year only
      // - If viewing PAST year (2025) YTD: compare full year to full year (2025 vs 2024)
      // - If viewing specific month: compare that month to same month previous year
      const currentMonth = new Date().getMonth() + 1; // 1-12
      const completedMonths = currentMonth - 1;
      let ytdMaxMonth = null;
      if (!monthParam && selectedYearValue === actualCurrentYear) {
        // Viewing current year YTD - only compare completed months
        ytdMaxMonth = completedMonths > 0 ? completedMonths : null;
      }
      // For past years or specific months, ytdMaxMonth stays null (full year comparison)
      
      const [firmRes, allUsersRes, allStatsRes, benchmarksRes, billingsRes, prevBillingsRes, financialsRes] = await Promise.all([
        statsApi.getFirmOverview(year, month),
        usersApi.getAll(),  // Get all users including partners
        statsApi.getAll(),
        benchmarksApi.get(),
        billingsApi.getSummary(selectedYearValue, monthParam, null),
        billingsApi.getSummary(selectedYearValue - 1, monthParam, ytdMaxMonth),
        axios.get(`${API_URL}/api/financials/comparison?year=${selectedYearValue}`, { headers: getAuthHeader() }).catch(() => ({ data: { data: [] } })),
      ]);

      setFirmData(firmRes.data);
      // Filter to users who track stats (employees + partners who track stats)
      const statsUsers = allUsersRes.data.filter(u => u.tracks_stats !== false);
      setEmployees(statsUsers);
      setAvailableYears(firmRes.data.available_years || [new Date().getFullYear()]);
      if (selectedYear === null) {
        setSelectedYear(firmRes.data.selected_year);
      }

      // Process financials data
      const finData = financialsRes.data?.data || [];
      const latestMonth = finData.length > 0 ? finData[finData.length - 1] : null;
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const chartData = finData.map(d => ({
        month: monthNames[d.month - 1],
        "A/R": d.accounts_receivable || 0,
        "Prior A/R": d.prev_accounts_receivable || 0,
        "WIP": d.work_in_progress || 0,
        "Prior WIP": d.prev_work_in_progress || 0,
        "Cash": d.cash_balance || 0,
        "Prior Cash": d.prev_cash_balance || 0,
      }));
      setFinancials({
        current: latestMonth,
        previous: latestMonth ? {
          accounts_receivable: latestMonth.prev_accounts_receivable,
          work_in_progress: latestMonth.prev_work_in_progress,
          cash_balance: latestMonth.prev_cash_balance,
        } : null,
        chartData,
      });

      // Set partner billings data
      setPartnerBillings({
        current: billingsRes.data.total_billings || 0,
        previous: prevBillingsRes.data.total_billings || 0,
        byPartner: billingsRes.data.by_partner || {},
        prevByPartner: prevBillingsRes.data.by_partner || {},
      });

      // Calculate per-employee stats for selected period
      const displayYear = year || firmRes.data.selected_year;
      const displayMonth = month;
      const stats = allStatsRes.data;
      const benchmarks = benchmarksRes.data;

      const employeeStatsMap = {};
      const prevEmployeeStatsMap = {};
      const prevYear = displayYear - 1;
      stats.forEach((stat) => {
        const matchesYear = stat.year === displayYear;
        const matchesPrevYear = stat.year === prevYear;
        // For YTD: only include completed months (not the current month)
        const matchesMonth = displayMonth ? stat.month === displayMonth : 
          (displayYear === new Date().getFullYear() ? stat.month < currentMonth : true);
        // For prior-year YTD comparison, mirror the current-year YTD window
        // (so e.g. Jan–Mar 2026 compares against Jan–Mar 2025).
        const matchesPrevMonth = displayMonth ? stat.month === displayMonth :
          (displayYear === new Date().getFullYear() ? stat.month < currentMonth : true);

        const accumulate = (bucket, statRow) => {
          if (!bucket[statRow.user_id]) {
            bucket[statRow.user_id] = {
              billable_hours: 0,
              total_hours: 0,
              plt_hours: 0,
              billed_amount: 0,
              realized_amount: 0,
              total_pay: 0,
            };
          }
          bucket[statRow.user_id].billable_hours += statRow.billable_hours;
          bucket[statRow.user_id].total_hours += statRow.total_hours;
          bucket[statRow.user_id].plt_hours += (statRow.plt_hours || 0);
          bucket[statRow.user_id].billed_amount += statRow.billed_amount;
          bucket[statRow.user_id].realized_amount += statRow.realized_amount;
          bucket[statRow.user_id].total_pay += statRow.total_pay;
        };

        if (matchesYear && matchesMonth) accumulate(employeeStatsMap, stat);
        if (matchesPrevYear && matchesPrevMonth) accumulate(prevEmployeeStatsMap, stat);
      });

      // Helper: compute the three percentages for a given stats bucket.
      const calcMetrics = (s) => {
        const workingHrs = s.total_hours - s.plt_hours;
        return {
          totalHours: s.total_hours,
          billablePercent: workingHrs > 0 ? (s.billable_hours / workingHrs) * 100 : 0,
          realizationPercent: s.billed_amount > 0 ? (s.realized_amount / s.billed_amount) * 100 : 0,
          billToPayPercent: s.total_pay > 0 ? (s.realized_amount / s.total_pay) * 100 : 0,
        };
      };

      const enrichedEmployees = statsUsers
        .map((emp) => {
        const empStats = employeeStatsMap[emp.id] || {
          billable_hours: 0,
          total_hours: 0,
          plt_hours: 0,
          billed_amount: 0,
          realized_amount: 0,
          total_pay: 0,
        };

        // Billable % = Billable Hours / (Total Hours - PLT Hours)
        const workingHours = empStats.total_hours - empStats.plt_hours;
        const billablePercent =
          workingHours > 0
            ? (empStats.billable_hours / workingHours) * 100
            : 0;
        const realizationPercent =
          empStats.billed_amount > 0
            ? (empStats.realized_amount / empStats.billed_amount) * 100
            : 0;
        // Bill to Pay % = Realized (Billed) / Total Pay
        const billToPayPercent =
          empStats.total_pay > 0
            ? (empStats.realized_amount / empStats.total_pay) * 100
            : 0;

        // Get the appropriate benchmarks for this user
        const isPartner = emp.tracks_billings === true && emp.name !== "Kristi Mendez";
        const billableTarget = isPartner ? (benchmarks.partner_billable_target || 50) : benchmarks.billable_target;
        const realizationTarget = isPartner ? (benchmarks.partner_realization_target || 90) : benchmarks.realization_target;
        const billToPayTarget = isPartner ? (benchmarks.partner_bill_to_pay_target || 100) : benchmarks.bill_to_pay_target;

        // Calculate percentage of target achieved for each metric
        const billablePctOfTarget = billableTarget > 0 ? (billablePercent / billableTarget) * 100 : 0;
        const realizationPctOfTarget = realizationTarget > 0 ? (realizationPercent / realizationTarget) * 100 : 0;
        const billToPayPctOfTarget = billToPayTarget > 0 ? (billToPayPercent / billToPayTarget) * 100 : 0;
        
        // Weighted score - Bill to Pay 50%, Billable 30%, Realization 20%
        let overallScore = (billToPayPctOfTarget * 0.50) + (billablePctOfTarget * 0.30) + (realizationPctOfTarget * 0.20);
        
        // Cap rules - can't hide weak metrics behind strong ones
        // If ANY metric is below 70% of target → cap at 74 (Needs Focus)
        if (billToPayPctOfTarget < 70 || billablePctOfTarget < 70 || realizationPctOfTarget < 70) {
          overallScore = Math.min(overallScore, 74);
        }
        // If Bill to Pay (most important) is below 80% of target → cap at 89 (Close)
        else if (billToPayPctOfTarget < 80) {
          overallScore = Math.min(overallScore, 89);
        }

        const prevStats = prevEmployeeStatsMap[emp.id] || {
          billable_hours: 0, total_hours: 0, plt_hours: 0,
          billed_amount: 0, realized_amount: 0, total_pay: 0,
        };
        const prev = calcMetrics(prevStats);

        return {
          ...emp,
          // Current period raw values exposed for print/preview tables
          totalHours: empStats.total_hours,
          billableHours: empStats.billable_hours,
          billedAmount: empStats.billed_amount,
          realizedAmount: empStats.realized_amount,
          billablePercent: billablePercent.toFixed(1),
          realizationPercent: realizationPercent.toFixed(1),
          billToPayPercent: billToPayPercent.toFixed(1),
          isPartner: isPartner,
          overallScore: overallScore.toFixed(0),
          meetsBillable: billablePercent >= billableTarget,
          meetsRealization: realizationPercent >= realizationTarget,
          meetsBillToPay: billToPayPercent >= billToPayTarget,
          // Prior-year comparison metrics (same YTD window or same month)
          prev_total_hours: prev.totalHours,
          prev_billable_percent: prev.billablePercent,
          prev_realization_percent: prev.realizationPercent,
          prev_bill_to_pay_percent: prev.billToPayPercent,
        };
      })
      // Filter out specific users that shouldn't appear in stats
      .filter(emp => !["Kathy Henson", "Tonia McKenzie"].includes(emp.name))
      .sort((a, b) => a.name.localeCompare(b.name));

      setEmployeeStats(enrichedEmployees);
    } catch (error) {
      console.error("Dashboard load error:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Only include employees with actual data in the chart
  const chartData = employeeStats
    .filter((emp) => parseFloat(emp.billablePercent) > 0 || parseFloat(emp.realizationPercent) > 0)
    .map((emp) => ({
      name: emp.name.split(" ")[0],
      "Billable %": parseFloat(parseFloat(emp.billablePercent).toFixed(2)),
      "Realization %": parseFloat(parseFloat(emp.realizationPercent).toFixed(2)),
      "Bill to Pay %": parseFloat((parseFloat(emp.billToPayPercent) / 10).toFixed(2)), // Scale down for chart
    }));

  // Get status badge based on overall score
  const getStatusBadge = (emp) => {
    const score = parseFloat(emp.overallScore);
    if (score >= 100) {
      return { label: "Exceeds", className: "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" };
    } else if (score >= 90) {
      return { label: "On Track", className: "bg-blue-500/20 text-blue-400 border border-blue-500/30" };
    } else if (score >= 75) {
      return { label: "Close", className: "bg-amber-500/20 text-amber-400 border border-amber-500/30" };
    } else {
      return { label: "Needs Focus", className: "bg-rose-500/20 text-rose-400 border border-rose-500/30" };
    }
  };

  const exportToExcel = () => {
    const data = employeeStats.map(emp => ({
      "Employee": emp.name,
      "Role": emp.isPartner ? "Partner" : "Employee",
      "Billable %": parseFloat(emp.billablePercent),
      "Realization %": parseFloat(emp.realizationPercent),
      "Bill to Pay %": parseFloat(emp.billToPayPercent),
      "Score": parseFloat(emp.overallScore),
      "Status": getStatusBadge(emp).label,
    }));

    // Add summary row
    data.push({
      "Employee": "---",
      "Role": "---",
      "Billable %": "---",
      "Realization %": "---",
      "Bill to Pay %": "---",
      "Score": "---",
      "Status": "---",
    });
    data.push({
      "Employee": "FIRM TOTALS",
      "Role": "",
      "Billable %": firmData?.current_year?.billable_percent || 0,
      "Realization %": firmData?.current_year?.realization_percent || 0,
      "Bill to Pay %": firmData?.current_year?.bill_to_pay_percent || 0,
      "Score": "",
      "Status": "",
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Performance Report");
    
    const period = getPeriodLabel().replace(/\s+/g, '_');
    XLSX.writeFile(wb, `Performance_Report_${period}.xlsx`);
    toast.success("Report exported to Excel");
  };

  const getPeriodLabel = () => {
    if (selectedMonth > 0) {
      return `${MONTHS[selectedMonth].label} ${selectedYear}`;
    }
    if (firmData?.period_label === "ytd") {
      return `Year-to-date ${selectedYear}`;
    }
    return `Full Year ${selectedYear}`;
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Firm Overview</h1>
          <p className="text-muted-foreground mt-1">
            {getPeriodLabel()} vs {firmData?.previous_year_value}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-muted-foreground" />
            <Select
              value={selectedYear?.toString()}
              onValueChange={(value) => setSelectedYear(parseInt(value))}
            >
              <SelectTrigger className="w-28" data-testid="year-selector">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedMonth.toString()}
              onValueChange={(value) => setSelectedMonth(parseInt(value))}
            >
              <SelectTrigger className="w-40" data-testid="month-selector">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((month) => (
                  <SelectItem key={month.value} value={month.value.toString()}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="gap-2" onClick={exportToExcel} data-testid="export-excel-btn">
            <FileSpreadsheet size={18} />
            Export
          </Button>
          <Link to="/employees">
            <Button variant="outline" className="gap-2" data-testid="manage-employees-btn">
              <Users size={18} />
              Employees
            </Button>
          </Link>
          <Link to="/stats">
            <Button className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="enter-stats-btn">
              <TrendingUp size={18} />
              Enter Stats
            </Button>
          </Link>
        </div>
      </div>

      <EncryptionRotationCard />

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="text-primary" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Employees</p>
                <p className="text-2xl font-bold text-foreground">
                  {firmData?.employee_count || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-500/10 border-emerald-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <TrendingUp className="text-emerald-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Billed</p>
                <p className="text-2xl font-bold text-foreground">
                  ${(partnerBillings.current || 0).toLocaleString()}
                </p>
                {partnerBillings.previous > 0 ? (() => {
                  const current = partnerBillings.current || 0;
                  const previous = partnerBillings.previous || 0;
                  const change = current - previous;
                  const percentChange = ((change / previous) * 100).toFixed(1);
                  const isUp = change >= 0;
                  return (
                    <p className={`text-xs mt-1 ${isUp ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {isUp ? '↑' : '↓'} ${Math.abs(change).toLocaleString()} ({isUp ? '+' : ''}{percentChange}%)
                    </p>
                  );
                })() : (
                  <p className="text-xs mt-1 text-muted-foreground">No prev year data</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-500/10 border-amber-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <DollarSign className="text-amber-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">$ per Billable Hour</p>
                {(() => {
                  const currentBilled = firmData?.current_year?.total_realized || 0;
                  const currentHours = firmData?.current_year?.total_billable_hours || 0;
                  const previousBilled = firmData?.previous_year?.total_realized || 0;
                  const previousHours = firmData?.previous_year?.total_billable_hours || 0;
                  const currentRate = currentHours > 0 ? currentBilled / currentHours : 0;
                  const previousRate = previousHours > 0 ? previousBilled / previousHours : 0;
                  const change = currentRate - previousRate;
                  const percentChange = previousRate > 0 ? ((change / previousRate) * 100).toFixed(1) : 0;
                  const isUp = change >= 0;
                  return (
                    <>
                      <p className="text-2xl font-bold text-foreground">
                        ${currentRate.toFixed(2)}
                      </p>
                      {previousRate > 0 ? (
                        <p className={`text-xs mt-1 ${isUp ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {isUp ? '↑' : '↓'} ${Math.abs(change).toFixed(2)} ({isUp ? '+' : ''}{percentChange}%)
                        </p>
                      ) : (
                        <p className="text-xs mt-1 text-muted-foreground">No prev year data</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KPICard
          title="Firm Billable %"
          value={firmData?.current_year?.billable_percent || 0}
          benchmark={firmData?.benchmarks?.billable_target}
          previousValue={firmData?.previous_year?.billable_percent}
          tooltip="Employees only (excludes partners). Billable hours divided by total working hours (excluding PLT & Holiday)."
        />

        <KPICard
          title="Firm Realization"
          value={firmData?.current_year?.realization_percent || 0}
          benchmark={firmData?.benchmarks?.realization_target}
          previousValue={firmData?.previous_year?.realization_percent}
          tooltip="All staff (employees + partners). Percentage of produced work that was actually billed to clients."
        />

        <KPICard
          title="Firm Bill to Pay"
          value={firmData?.current_year?.bill_to_pay_percent || 0}
          benchmark={firmData?.benchmarks?.bill_to_pay_target}
          previousValue={firmData?.previous_year?.bill_to_pay_percent}
          tooltip="Employees only (excludes partners). Produced amount divided by total payroll."
        />
      </div>

      {/* Financials Overview - A/R, WIP, Cash */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Wallet className="text-purple-500" size={20} />
            A/R, WIP & Cash
          </CardTitle>
          <Link to="/financials">
            <Button variant="ghost" size="sm" className="text-purple-500">
              View Details <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {financials.chartData.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <p className="text-xs text-purple-500 font-medium">Accounts Receivable</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground">
                  ${(financials.current?.accounts_receivable || 0).toLocaleString()}
                </p>
                {financials.previous?.accounts_receivable > 0 && (() => {
                  const current = financials.current?.accounts_receivable || 0;
                  const previous = financials.previous?.accounts_receivable || 0;
                  const change = ((current - previous) / previous * 100).toFixed(1);
                  const isUp = current >= previous;
                  return (
                    <p className={`text-xs mt-1 flex items-center gap-1 whitespace-nowrap ${isUp ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {isUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      <span>{isUp ? '+' : ''}{change}% vs PY</span>
                    </p>
                  );
                })()}
                <p className="text-xs text-muted-foreground mt-1">PY: ${(financials.previous?.accounts_receivable || 0).toLocaleString()}</p>
              </div>
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
                <p className="text-xs text-blue-500 font-medium">Work in Progress</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground">
                  ${(financials.current?.work_in_progress || 0).toLocaleString()}
                </p>
                {financials.previous?.work_in_progress > 0 && (() => {
                  const current = financials.current?.work_in_progress || 0;
                  const previous = financials.previous?.work_in_progress || 0;
                  const change = ((current - previous) / previous * 100).toFixed(1);
                  const isUp = current >= previous;
                  return (
                    <p className={`text-xs mt-1 flex items-center gap-1 whitespace-nowrap ${isUp ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {isUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      <span>{isUp ? '+' : ''}{change}% vs PY</span>
                    </p>
                  );
                })()}
                <p className="text-xs text-muted-foreground mt-1">PY: ${(financials.previous?.work_in_progress || 0).toLocaleString()}</p>
              </div>
              <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <p className="text-xs text-emerald-500 font-medium">Cash Balance</p>
                <p className="text-xl sm:text-2xl font-bold text-foreground">
                  ${(financials.current?.cash_balance || 0).toLocaleString()}
                </p>
                {financials.previous?.cash_balance > 0 && (() => {
                  const current = financials.current?.cash_balance || 0;
                  const previous = financials.previous?.cash_balance || 0;
                  const change = ((current - previous) / previous * 100).toFixed(1);
                  const isUp = current >= previous;
                  return (
                    <p className={`text-xs mt-1 flex items-center gap-1 whitespace-nowrap ${isUp ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {isUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      <span>{isUp ? '+' : ''}{change}% vs PY</span>
                    </p>
                  );
                })()}
                <p className="text-xs text-muted-foreground mt-1">PY: ${(financials.previous?.cash_balance || 0).toLocaleString()}</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Wallet className="mx-auto mb-2" size={32} />
              <p>No financial data yet for {selectedYear}</p>
              <Link to="/financials">
                <Button variant="link" size="sm" className="mt-2">Add Data</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Partner Billings Breakdown */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <DollarSign size={20} className="text-amber-500" />
            Partner Billings by Partner
          </CardTitle>
          <Link to="/billings">
            <Button variant="outline" size="sm" className="gap-1">
              {Object.keys(partnerBillings.byPartner).length > 0 ? 'Manage' : 'Add Billings'} <ArrowRight size={14} />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {Object.keys(partnerBillings.byPartner).length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {Object.entries(partnerBillings.byPartner)
                .sort((a, b) => a[1].name.localeCompare(b[1].name))
                .map(([userId, data]) => {
                  const prevData = partnerBillings.prevByPartner?.[userId];
                  const prevTotal = prevData?.total || 0;
                  const currentTotal = data.total;
                  const change = currentTotal - prevTotal;
                  const percentChange = prevTotal > 0 ? ((change / prevTotal) * 100).toFixed(1) : null;
                  const isUp = change >= 0;
                  
                  return (
                    <div key={userId} className="bg-muted rounded-lg p-4 text-center">
                      <p className="font-medium text-foreground truncate">{data.name}</p>
                      <p className="text-xl font-bold text-amber-500">${data.total.toLocaleString()}</p>
                      {prevTotal > 0 ? (
                        <p className={`text-xs mt-1 ${isUp ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {isUp ? '↑' : '↓'} {isUp ? '+' : ''}{percentChange}% vs prev yr
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">No prev yr data</p>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <DollarSign className="mx-auto h-10 w-10 text-muted-foreground/50 mb-2" />
              <p>No partner billings recorded yet</p>
              <p className="text-sm mt-1">
                Enable billing tracking for partners in <Link to="/employees" className="text-primary underline">Employees</Link>, 
                then add billings in <Link to="/billings" className="text-primary underline">Partner Billings</Link>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Gauge visualization */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target size={20} className="text-blue-600" />
            Benchmark Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap justify-around gap-8 py-4">
            <BenchmarkGauge
              current={firmData?.current_year?.billable_percent || 0}
              target={firmData?.benchmarks?.billable_target || 65}
              label="Billable %"
              size="lg"
            />
            <BenchmarkGauge
              current={firmData?.current_year?.realization_percent || 0}
              target={firmData?.benchmarks?.realization_target || 90}
              label="Realization"
              size="lg"
            />
            <BenchmarkGauge
              current={firmData?.current_year?.bill_to_pay_percent || 0}
              target={firmData?.benchmarks?.bill_to_pay_target || 225}
              label="Bill to Pay"
              size="lg"
            />
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Employee Performance Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-96 sm:h-96 overflow-x-auto" data-testid="performance-chart">
              <div className="min-w-[600px] h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={chartData} 
                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                    barCategoryGap="15%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: "#94a3b8", fontSize: 11 }} 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                    />
                    <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#1e293b",
                        border: "1px solid #475569",
                        borderRadius: "8px",
                        color: "#f1f5f9",
                      }}
                      labelStyle={{ color: "#f1f5f9" }}
                      formatter={(value, name) => {
                        if (name === "Bill to Pay %") {
                          return [`${(value * 10).toFixed(2)}%`, "Bill to Pay %"];
                        }
                        return [`${value.toFixed(2)}%`, name];
                      }}
                    />
                    <Legend />
                    <Bar dataKey="Billable %" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Realization %" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Bill to Pay %" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              * Bill to Pay % is scaled down by 10x for chart readability. Scroll horizontally on mobile to see all employees.
            </p>
          </CardContent>
        </Card>
      )}

      {/* AI Insights */}
      <Card className="border-purple-500/20 bg-purple-500/5">
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="text-purple-500" size={20} />
            <CardTitle className="text-foreground">AI Performance Insights</CardTitle>
          </div>
          <Button 
            onClick={loadAiInsights} 
            disabled={loadingInsights}
            className="gap-2 bg-purple-600 hover:bg-purple-700"
            data-testid="generate-insights-btn"
          >
            {loadingInsights ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles size={16} />
                {aiInsights ? "Refresh Insights" : "Generate Insights"}
              </>
            )}
          </Button>
        </CardHeader>
        <CardContent>
          {aiInsights ? (
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <div className="whitespace-pre-wrap text-foreground leading-relaxed"
                   dangerouslySetInnerHTML={{ 
                     __html: aiInsights.insights
                       .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
                       .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold text-purple-500 mt-4 mb-2">$1</h3>')
                       .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-purple-400 mt-4 mb-2">$1</h2>')
                       .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>')
                       .replace(/^(\d+)\. (.*$)/gm, '<li class="ml-4"><span class="font-medium">$1.</span> $2</li>')
                   }}
              />
              <p className="text-xs text-muted-foreground mt-4">
                Generated: {new Date(aiInsights.generated_at).toLocaleString()} | {aiInsights.employee_count} employees analyzed
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Sparkles className="h-12 w-12 mx-auto mb-3 text-purple-500/50" />
              <p>Click "Generate Insights" to get AI-powered analysis of your team's performance.</p>
              <p className="text-sm text-muted-foreground mt-1">Includes rankings, top performers, and improvement areas.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-foreground">Employee Statistics</CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-1"
              onClick={() => setShowPrintView(true)}
              data-testid="print-report-btn"
            >
              <Printer size={16} />
              Print Report
            </Button>
            <Link to="/employees">
              <Button variant="ghost" size="sm" className="gap-1" data-testid="view-all-employees">
                View All
                <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <Table data-testid="employee-stats-table">
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead className="text-right">Billable %</TableHead>
                <TableHead className="text-right">Realization %</TableHead>
                <TableHead className="text-right">Bill to Pay %</TableHead>
                <TableHead className="text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employeeStats.map((emp) => (
                <TableRow key={emp.id} className="table-row-hover">
                  <TableCell className="font-medium">
                    {emp.name}
                    {emp.isPartner && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                        Partner
                      </span>
                    )}
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono ${
                      emp.meetsBillable ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {emp.billablePercent}%
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono ${
                      emp.meetsRealization ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {emp.realizationPercent}%
                  </TableCell>
                  <TableCell
                    className={`text-right font-mono ${
                      emp.meetsBillToPay ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {emp.billToPayPercent}%
                  </TableCell>
                  <TableCell className="text-center">
                    {(() => {
                      const status = getStatusBadge(emp);
                      return (
                        <span 
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                          title={`Overall score: ${emp.overallScore}% of benchmarks`}
                        >
                          {status.label}
                        </span>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Print Report Modal */}
      {showPrintView && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-auto shadow-2xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-4 flex justify-between items-center z-10">
              <h2 className="text-lg font-semibold text-white">Employee Performance Report Preview</h2>
              <div className="flex gap-2">
                <Button 
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    // Exclude partners from the printable report — only line-level staff
                    const nonPartners = employeeStats.filter(e => !e.isPartner);
                    // Format a metric + delta vs prior year. Returns HTML.
                    const fmtPct = (cur, prev) => {
                      const c = parseFloat(cur || 0);
                      const p = parseFloat(prev || 0);
                      const delta = c - p;
                      const color = delta > 0 ? '#22543d' : delta < 0 ? '#742a2a' : '#4a5568';
                      const arrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
                      return `<div style="font-weight:600;">${c.toFixed(1)}%</div>
                        <div style="font-size:7pt;color:${color};">${arrow} ${Math.abs(delta).toFixed(1)} vs ${p.toFixed(1)}%</div>`;
                    };
                    const fmtHrs = (cur, prev) => {
                      const c = parseFloat(cur || 0);
                      const p = parseFloat(prev || 0);
                      const delta = c - p;
                      const color = delta > 0 ? '#22543d' : delta < 0 ? '#742a2a' : '#4a5568';
                      const arrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
                      return `<div style="font-weight:600;">${c.toFixed(1)}</div>
                        <div style="font-size:7pt;color:${color};">${arrow} ${Math.abs(delta).toFixed(1)} vs ${p.toFixed(1)}</div>`;
                    };
                    const reportData = {
                      period: selectedMonth === 0 
                        ? `Year-to-Date ${selectedYear}` 
                        : `${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear}`,
                      priorPeriod: selectedMonth === 0
                        ? `vs Year-to-Date ${selectedYear - 1}`
                        : `vs ${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear - 1}`,
                      generated: new Date().toLocaleDateString(),
                      totalEmployees: nonPartners.length,
                      avgBillable: nonPartners.length > 0 
                        ? (nonPartners.reduce((sum, e) => sum + parseFloat(e.billablePercent || 0), 0) / nonPartners.length).toFixed(1)
                        : 0,
                      avgRealization: nonPartners.length > 0 
                        ? (nonPartners.reduce((sum, e) => sum + parseFloat(e.realizationPercent || 0), 0) / nonPartners.length).toFixed(1)
                        : 0,
                      avgBillToPay: nonPartners.length > 0 
                        ? (nonPartners.reduce((sum, e) => sum + parseFloat(e.billToPayPercent || 0), 0) / nonPartners.length).toFixed(1)
                        : 0,
                      employees: [...nonPartners]
                        .sort((a, b) => parseFloat(b.overallScore || 0) - parseFloat(a.overallScore || 0))
                    };
                    
                    const employeeRows = reportData.employees.map((emp, idx) => {
                      const status = getStatusBadge(emp);
                      const statusStyle = status.label === 'Exceeds' ? 'background:#c6f6d5;color:#22543d;'
                        : status.label === 'On Track' ? 'background:#bee3f8;color:#2a4365;'
                        : status.label === 'Close' ? 'background:#fefcbf;color:#744210;'
                        : 'background:#fed7d7;color:#742a2a;';
                      return `<tr style="background:${idx % 2 === 0 ? '#f7fafc' : 'white'}">
                        <td style="padding:6px;border-bottom:1px solid #e2e8f0;font-weight:500;">${emp.name}</td>
                        <td style="padding:6px;border-bottom:1px solid #e2e8f0;text-align:right;">${fmtHrs(emp.totalHours, emp.prev_total_hours)}</td>
                        <td style="padding:6px;border-bottom:1px solid #e2e8f0;text-align:right;">${fmtPct(emp.billablePercent, emp.prev_billable_percent)}</td>
                        <td style="padding:6px;border-bottom:1px solid #e2e8f0;text-align:right;">${fmtPct(emp.realizationPercent, emp.prev_realization_percent)}</td>
                        <td style="padding:6px;border-bottom:1px solid #e2e8f0;text-align:right;">${fmtPct(emp.billToPayPercent, emp.prev_bill_to_pay_percent)}</td>
                        <td style="padding:6px;border-bottom:1px solid #e2e8f0;text-align:center;"><span style="${statusStyle}display:inline-block;padding:2px 8px;border-radius:10px;font-size:8pt;font-weight:600;">${status.label}</span></td>
                      </tr>`;
                    }).join('');
                    
                    printWindow.document.write(`<!DOCTYPE html><html><head><title>Employee Performance Report</title>
                      <style>
                        *{margin:0;padding:0;box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
                        html,body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
                        body{font-family:'Segoe UI',Arial,sans-serif;color:#1a1a1a;line-height:1.4;padding:0.5in;font-size:11pt;}
                        @media print{
                          body{padding:0;}
                          @page{margin:0.5in;}
                          /* Belt-and-suspenders: ensure colored cells & badges actually print */
                          *,*::before,*::after{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;}
                        }
                      </style></head><body>
                      <div style="text-align:center;border-bottom:2px solid #1e3a8a;padding-bottom:20px;margin-bottom:24px;">
                        <h1 style="font-size:22pt;color:#1e3a8a;margin-bottom:4px;font-weight:700;">Steele Martin Jones CPAs</h1>
                        <h2 style="font-size:14pt;color:#4a5568;font-weight:500;margin-bottom:8px;">Employee Performance Report</h2>
                        <p style="font-size:10pt;color:#718096;">${reportData.period}</p>
                        <p style="font-size:9pt;color:#a0aec0;">${reportData.priorPeriod}</p>
                        <p style="font-size:9pt;color:#a0aec0;margin-top:4px;">Generated: ${reportData.generated}</p>
                      </div>
                      <div style="margin-bottom:24px;">
                        <h3 style="font-size:13pt;color:#1e3a8a;border-bottom:1px solid #cbd5e0;padding-bottom:6px;margin-bottom:12px;font-weight:600;">Firm Summary <span style="font-size:9pt;font-weight:400;color:#718096;">(Staff only — partners excluded)</span></h3>
                        <div style="display:flex;gap:16px;">
                          <div style="flex:1;background:#f7fafc;border:1px solid #e2e8f0;padding:12px;border-radius:6px;text-align:center;">
                            <div style="font-size:9pt;color:#718096;margin-bottom:4px;">Total Staff</div>
                            <div style="font-size:18pt;font-weight:700;color:#1a202c;">${reportData.totalEmployees}</div>
                          </div>
                          <div style="flex:1;background:#f7fafc;border:1px solid #e2e8f0;padding:12px;border-radius:6px;text-align:center;">
                            <div style="font-size:9pt;color:#718096;margin-bottom:4px;">Avg Billable %</div>
                            <div style="font-size:18pt;font-weight:700;color:#1a202c;">${reportData.avgBillable}%</div>
                          </div>
                          <div style="flex:1;background:#f7fafc;border:1px solid #e2e8f0;padding:12px;border-radius:6px;text-align:center;">
                            <div style="font-size:9pt;color:#718096;margin-bottom:4px;">Avg Realization %</div>
                            <div style="font-size:18pt;font-weight:700;color:#1a202c;">${reportData.avgRealization}%</div>
                          </div>
                          <div style="flex:1;background:#f7fafc;border:1px solid #e2e8f0;padding:12px;border-radius:6px;text-align:center;">
                            <div style="font-size:9pt;color:#718096;margin-bottom:4px;">Avg Bill to Pay %</div>
                            <div style="font-size:18pt;font-weight:700;color:#1a202c;">${reportData.avgBillToPay}%</div>
                          </div>
                        </div>
                      </div>
                      <div style="margin-bottom:24px;">
                        <h3 style="font-size:13pt;color:#1e3a8a;border-bottom:1px solid #cbd5e0;padding-bottom:6px;margin-bottom:12px;font-weight:600;">Individual Performance <span style="font-size:9pt;font-weight:400;color:#718096;">(each cell shows current period and change vs prior year)</span></h3>
                        <table style="width:100%;border-collapse:collapse;font-size:9pt;">
                          <thead><tr style="background:#1e3a8a;color:white;">
                            <th style="padding:8px 6px;text-align:left;font-weight:600;font-size:8pt;text-transform:uppercase;color:white;">Employee</th>
                            <th style="padding:8px 6px;text-align:right;font-weight:600;font-size:8pt;text-transform:uppercase;color:white;">Total Hrs</th>
                            <th style="padding:8px 6px;text-align:right;font-weight:600;font-size:8pt;text-transform:uppercase;color:white;">Billable %</th>
                            <th style="padding:8px 6px;text-align:right;font-weight:600;font-size:8pt;text-transform:uppercase;color:white;">Realization %</th>
                            <th style="padding:8px 6px;text-align:right;font-weight:600;font-size:8pt;text-transform:uppercase;color:white;">Bill to Pay %</th>
                            <th style="padding:8px 6px;text-align:center;font-weight:600;font-size:8pt;text-transform:uppercase;color:white;">Status</th>
                          </tr></thead>
                          <tbody>${employeeRows}</tbody>
                        </table>
                      </div>
                      <div style="margin-top:20px;padding-top:12px;border-top:1px solid #e2e8f0;">
                        <h4 style="font-size:9pt;color:#4a5568;margin-bottom:6px;font-weight:600;">Performance Legend</h4>
                        <div style="display:flex;gap:20px;font-size:8pt;color:#718096;">
                          <span><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#48bb78;margin-right:4px;vertical-align:middle;"></span> Exceeds: 100%+</span>
                          <span><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#4299e1;margin-right:4px;vertical-align:middle;"></span> On Track: 90-99%</span>
                          <span><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#ecc94b;margin-right:4px;vertical-align:middle;"></span> Close: 75-89%</span>
                          <span><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:#fc8181;margin-right:4px;vertical-align:middle;"></span> Needs Focus: &lt;75%</span>
                        </div>
                      </div>
                      <div style="margin-top:24px;padding-top:12px;border-top:1px solid #e2e8f0;text-align:center;font-size:8pt;color:#a0aec0;">
                        <p>Confidential - For Internal Use Only</p>
                        <p>Steele Martin Jones CPAs | ${new Date().getFullYear()}</p>
                      </div>
                    </body></html>`);
                    printWindow.document.close();
                    setTimeout(() => printWindow.print(), 250);
                  }}
                  className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Printer size={16} />
                  Print Report
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowPrintView(false)}
                  className="bg-white text-gray-800 border-gray-300 hover:bg-gray-100"
                >
                  Close
                </Button>
              </div>
            </div>
            
            {/* Preview Content */}
            <div className="p-8 bg-white text-gray-900">
              {/* Report Header */}
              <div className="text-center border-b-2 border-blue-900 pb-5 mb-6">
                <h1 className="text-2xl font-bold text-blue-900 mb-1">Steele Martin Jones CPAs</h1>
                <h2 className="text-lg text-gray-600 mb-2">Employee Performance Report</h2>
                <p className="text-gray-500">
                  {selectedMonth === 0 
                    ? `Year-to-Date ${selectedYear}` 
                    : `${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
                </p>
                <p className="text-sm text-gray-400 mt-1">Generated: {new Date().toLocaleDateString()}</p>
              </div>

              {/* Firm Summary */}
              <div className="mb-6">
                <h3 className="text-base font-semibold text-blue-900 border-b border-gray-200 pb-2 mb-4">
                  Firm Summary <span className="text-xs font-normal text-gray-500">(Staff only — partners excluded)</span>
                </h3>
                {(() => {
                  const nonPartners = employeeStats.filter(e => !e.isPartner);
                  return (
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                        <p className="text-xs text-gray-500 mb-1">Total Staff</p>
                        <p className="text-2xl font-bold text-gray-900">{nonPartners.length}</p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                        <p className="text-xs text-gray-500 mb-1">Avg Billable %</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {nonPartners.length > 0
                            ? (nonPartners.reduce((sum, e) => sum + parseFloat(e.billablePercent || 0), 0) / nonPartners.length).toFixed(1)
                            : 0}%
                        </p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                        <p className="text-xs text-gray-500 mb-1">Avg Realization %</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {nonPartners.length > 0
                            ? (nonPartners.reduce((sum, e) => sum + parseFloat(e.realizationPercent || 0), 0) / nonPartners.length).toFixed(1)
                            : 0}%
                        </p>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg text-center">
                        <p className="text-xs text-gray-500 mb-1">Avg Bill to Pay %</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {nonPartners.length > 0
                            ? (nonPartners.reduce((sum, e) => sum + parseFloat(e.billToPayPercent || 0), 0) / nonPartners.length).toFixed(1)
                            : 0}%
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Employee Table */}
              <div className="mb-6">
                <h3 className="text-base font-semibold text-blue-900 border-b border-gray-200 pb-2 mb-4">
                  Individual Performance{" "}
                  <span className="text-xs font-normal text-gray-500">
                    (each cell shows current period and change vs prior year:{" "}
                    {selectedMonth === 0
                      ? `Year-to-Date ${selectedYear - 1}`
                      : `${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear - 1}`}
                    )
                  </span>
                </h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-900 text-white">
                      <th className="py-2 px-3 text-left font-semibold text-xs uppercase">Employee</th>
                      <th className="py-2 px-3 text-right font-semibold text-xs uppercase">Total Hrs</th>
                      <th className="py-2 px-3 text-right font-semibold text-xs uppercase">Billable %</th>
                      <th className="py-2 px-3 text-right font-semibold text-xs uppercase">Realization %</th>
                      <th className="py-2 px-3 text-right font-semibold text-xs uppercase">Bill to Pay %</th>
                      <th className="py-2 px-3 text-center font-semibold text-xs uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...employeeStats]
                      .filter(e => !e.isPartner)
                      .sort((a, b) => parseFloat(b.overallScore || 0) - parseFloat(a.overallScore || 0))
                      .map((emp, idx) => {
                        const status = getStatusBadge(emp);
                        // Renders the "current" number + a small "Δ vs prior" sub-line
                        const renderDelta = (cur, prev, suffix = "") => {
                          const c = parseFloat(cur || 0);
                          const p = parseFloat(prev || 0);
                          const d = c - p;
                          const color = d > 0 ? "text-emerald-700" : d < 0 ? "text-rose-700" : "text-gray-500";
                          const arrow = d > 0 ? "↑" : d < 0 ? "↓" : "→";
                          return (
                            <>
                              <div className="font-semibold text-gray-900">{c.toFixed(1)}{suffix}</div>
                              <div className={`text-[10px] ${color}`}>
                                {arrow} {Math.abs(d).toFixed(1)} vs {p.toFixed(1)}{suffix}
                              </div>
                            </>
                          );
                        };
                        return (
                          <tr key={emp.id} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                            <td className="py-2 px-3 font-medium text-gray-900 border-b border-gray-200">{emp.name}</td>
                            <td className="py-2 px-3 text-right text-gray-700 border-b border-gray-200">
                              {renderDelta(emp.totalHours, emp.prev_total_hours)}
                            </td>
                            <td className="py-2 px-3 text-right text-gray-700 border-b border-gray-200">
                              {renderDelta(emp.billablePercent, emp.prev_billable_percent, "%")}
                            </td>
                            <td className="py-2 px-3 text-right text-gray-700 border-b border-gray-200">
                              {renderDelta(emp.realizationPercent, emp.prev_realization_percent, "%")}
                            </td>
                            <td className="py-2 px-3 text-right text-gray-700 border-b border-gray-200">
                              {renderDelta(emp.billToPayPercent, emp.prev_bill_to_pay_percent, "%")}
                            </td>
                            <td className="py-2 px-3 text-center border-b border-gray-200">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold
                                ${status.label === 'Exceeds' ? 'bg-emerald-100 text-emerald-800' : ''}
                                ${status.label === 'On Track' ? 'bg-blue-100 text-blue-800' : ''}
                                ${status.label === 'Close' ? 'bg-amber-100 text-amber-800' : ''}
                                ${status.label === 'Needs Focus' ? 'bg-rose-100 text-rose-800' : ''}
                              `}>
                                {status.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              {/* Legend */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-600 mb-2">Performance Legend</h4>
                <div className="flex gap-6 text-xs text-gray-500">
                  <span><span className="inline-block w-3 h-3 bg-emerald-500 rounded-full mr-1 align-middle"></span> Exceeds: 100%+</span>
                  <span><span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-1 align-middle"></span> On Track: 90-99%</span>
                  <span><span className="inline-block w-3 h-3 bg-amber-500 rounded-full mr-1 align-middle"></span> Close: 75-89%</span>
                  <span><span className="inline-block w-3 h-3 bg-rose-500 rounded-full mr-1 align-middle"></span> Needs Focus: &lt;75%</span>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-gray-200 text-center text-xs text-gray-400">
                <p>Confidential - For Internal Use Only</p>
                <p>Steele Martin Jones CPAs | {new Date().getFullYear()}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
